<template lang="html">
  <section :id="id" class="custom-display-element-manifest-config">
    <!-- Existing member fields from the manifest -->
    <custom-display-element-member-row
      v-for="(member, index) in membersList"
      :key="index"
      :id="`widget-version-member-row-${member.name}`"
      :translations="translations"
      :member="member"
      :all-sources="allSources"
      :properties="properties"
      :custom-member-fields="customMemberFields"
      :app-type="appContent.type"
      @on-expression-selected="onExpressionSelected"
      @on-member-prop-expression-selected="onMemberPropExpressionSelected"
      @on-member-data-source-selected="onMemberDataSourceSelected"
      @on-member-prop-data-delete="onMemberPropDataDelete"
    />

    <!-- List of custom member fields, these are not in the manifest  -->
    <custom-display-element-member-row
      v-for="(member, index) in customMemberList"
      :key="`${newMemberPrefix}-${index}`"
      :id="`${newMemberPrefix}-${index}`"
      :member="member"
      :all-sources="allSources"
      :properties="properties"
      :custom-member-properties="customMemberProperties"
      :custom-member-fields="customMemberFields"
      :app-type="appContent.type"
      @on-custom-member-remove="onCustomMemberRemove"
      @on-expression-selected="onExpressionSelected"
      @on-member-prop-expression-selected="onMemberPropExpressionSelected"
      @on-member-data-source-selected="onMemberDataSourceSelected"
      @on-member-prop-data-delete="onMemberPropDataDelete"
    />
    <div class="loader" v-if="displayLoader"></div>
    <div class="add-new" v-if="displayAddNew">
      <button class="m-button" @click.stop="toggleAddNew">
        {{ $t("addNew") }}
      </button>
    </div>
  </section>
</template>

<script lang="js">
  import { MUTATION_NAME, TOAST_OPTIONS } from "@/static/global-variables";
  import distributionChannelUtil from "@/utils/distribution-channel-util";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import DistributionChannelsService from "@/services/distributionChannelsService";
  import CustomDisplayElementMemberRow from "@/components/custom-display-elements/page-elements/CustomDisplayElementMemberRow";

  export default  {
    name: "custom-display-element-manifest-config",
    components: {
      CustomDisplayElementMemberRow
    },
    props: {
      id: String,
      widgetData: {
        type: Object,
        default: function() {
          return {};
        }
      }
    },
    data () {
      return {
        displayLoader: false
      }
    },
    computed: {
      appId() {
        return this.$route.params.appId;
      },
      newMemberPrefix() {
        return "custom-field-member";
      },
      userLocale() {
        return this.$store.getters.userLocale || "en_US";
      },
      messages() {
        return this.$store.state.messages;
      },
      widgetUuid() {
        return this.widgetData.uuid;
      },
      widgetVersionUuid() {
        return distributionChannelUtil.findWidgetVersionUuid(this.widgetData.widget.versions, this.widgetVersion);
      },
      widgetVersion() {
        return this.widgetData.newVersion || this.widgetData.version;
      },
      widgetDetails() {
        return this.$store.getters[MUTATION_NAME.ALL_WIDGET_VERSION_DETAILS][this.widgetVersionUuid];
      },
      members() {
        const members = this.widgetDetails && Object.keys(this.widgetDetails).length? this.widgetDetails.manifest.modules[0].declarations[0].members : [];
        return distributionChannelUtil.filterMembers(members);
      },
      membersList() {
        return distributionChannelUtil.formatWidgetVersionMembers(JSON.parse(JSON.stringify(this.members)));
      },
      customMemberList() {
        return this.widgetData.customMembers.members;
      },
      displayAddNew() {
        return !this.displayLoader && Boolean(this.widgetDetails);
      },
      customMemberProperties() {
        return this.widgetData.customMembers.properties;
      },
      properties() {
        return this.widgetData.properties;
      },
      translations() {
        return this.widgetDetails? distributionChannelUtil.findWidgetTranslations(this.widgetDetails.translations, this.userLocale) : {};
      },
      customMemberFields() {
        return this.widgetData.customMemberFields;
      },
      appContent() {
        return this.appId? JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId))) : null;
      },
      allSources() {
        let output = null;
        if(this.appId) {
          if (this.appContent) {
            output = this.appContent.dataElements || {};
          } else {
            console.error(`No app content found for appId ${this.appId}`);
          }
        }
        return output;
      }
    },
    watch: {
      widgetVersionUuid: function() {
        this.getVersionDetails(this.widgetUuid, this.widgetVersionUuid);
      },
      membersList: function() {
        if(this.membersList.length) {
          if(!Object.keys(this.properties).length) {
            this.initProperties();
          } else {
            this.setupExistingProperties();
          }
        }
      }
    },
    methods: {
      getVersionDetails(widgetUuid, widgetVersionUuid) {
        if(!this.widgetDetails) {
          this.displayLoader = true;
          DistributionChannelsService.getWidgetVersionDetails(widgetUuid, widgetVersionUuid)
          .catch((e) => {
            this.$bvToast.toast(this.getMessageText(this.messages.ERROR_WHILE_LOADING), TOAST_OPTIONS.bottomCenter);
            console.error(e);
          })
          .finally(() => {
            this.displayLoader = false;
            this.$emit("on-manifest-config-ready", true);
          });
        } else {
          this.$emit("on-manifest-config-ready", true);
        }
      },
      toggleAddNew() {
        distributionChannelUtil.addNewCustomMember(this.widgetData);
      },
      onExpressionSelected(ctx) {
        if(ctx.member.customId) {
          this.updateCustomProperty(ctx);
        } else {
          this.updateProperty(ctx);
        }
        this.commitWidgetData();
      },
      updateCustomProperty(ctx) {
        // this is a property from a custom member field
        const customProperty = this.widgetData.customMembers.properties[ctx.member.customId];
        customProperty.name = ctx.member.name;
        customProperty.type = ctx.expression ? ctx.expression.dataType : "";
        customProperty.expression = ctx.expression;
        const member = this.customMemberList.find((e) =>
          e.customId === ctx.member.customId
        );
        member.type = JSON.parse(JSON.stringify(ctx.member.type));
      },
      updateProperty(ctx) {
        // this is property constructed from the manifest
        this.properties[ctx.member.name].expression = ctx.expression;
      },
      onMemberPropExpressionSelected(ctx) {
        if(ctx.memberProp.customId) {
          this.updateCustomChildProperty(ctx);
        } else {
          this.updateChildProperty(ctx);
        }
        this.commitWidgetData();
      },
      updateCustomChildProperty(ctx) {
        // this is a child property from a custom member field
        const customFields = this.widgetData.customMemberFields[ctx.member.name] || {};
        customFields[ctx.memberProp.customId] = {
          memberProp: ctx.memberProp,
          expression: ctx.expression
        };
        this.widgetData.customMemberFields[ctx.member.name] = customFields;
      },
      updateChildProperty(ctx) {
        // this is a child property constructed from the manifest
        this.properties[ctx.member.name].fields[ctx.memberProp.property] = ctx.expression;
      },
      onMemberDataSourceSelected(ctx) {
        this.updateMemberDataSource(ctx);
        this.commitWidgetData();
      },
      onMemberPropDataDelete(ctx) {
        const customFields = this.widgetData.customMemberFields[ctx.member.name];
        delete customFields[ctx.memberProp.customId];
        if(!Object.keys(customFields).length) {
          delete this.widgetData.customMemberFields[ctx.member.name];
        }
        this.commitWidgetData();
      },
      updateMemberDataSource(ctx) {
        // this is a member from the manifest
        this.properties[ctx.member.name].dataSource = ctx.dataSource? ctx.dataSource.id : "";
        if (ctx.dataSource === null) {
          for (let fieldName of Object.keys(this.properties[ctx.member.name].fields)) {
            this.properties[ctx.member.name].fields[fieldName] = null;
          }
        }
      },
      onCustomMemberRemove(ctx) {
        this.widgetData.customMembers.members = this.widgetData.customMembers.members.filter(e => e.customId !== ctx.member.customId);
        delete this.widgetData.customMembers.properties[ctx.member.customId];
        this.commitWidgetData();
      },
      initProperties() {
        distributionChannelUtil.initProperties(this.properties, this.membersList);
        this.commitWidgetData();
      },
      commitWidgetData() {
        this.$store.commit(MUTATION_NAME.ADD_EDIT_WIDGET_DATA, this.widgetData);
      },
      setupExistingProperties() {
        distributionChannelUtil.setupExistingProperties(this.widgetData, this.membersList);
        // update version here since widgetVersion may be
        // the newVersion used to downgrade or upgrade
        this.widgetData.version = this.widgetVersion;
        this.commitWidgetData();
      }
    }
  }
</script>

<style scoped lang="scss">
  .custom-display-element-manifest-config {
    position: relative;
    .add-new {
      padding: 1rem;
    }
    .loader {
      width: 2rem;
      height: 2rem;
      margin: auto;
    }
  }
</style>
